import { useQuery } from '@tanstack/react-query';

import { useInstanceId } from '@frontend/hooks';

import { ensureSystemAgent } from '../services/system-agents';

export type UseSystemAgentResult = {
  /** The system agent's per-instance UUID, or null until resolved / if unprovisionable. */
  agentUuid: string | null;
  /** True once the UUID has resolved. */
  ready: boolean;
  isPending: boolean;
  error: unknown;
};

/**
 * Resolves a managed platform system agent's per-instance UUID via the backend ensure
 * endpoint, which installs it as a system agent on demand if it isn't already present.
 *
 * Shared by the Stickyboard Steward and the VAU Piere persona generator: both are
 * frontend-resolved system agents that must exist + be `is_system_agent` before use, and
 * neither reliably appears in the specialized-agents list until it's been installed.
 * Resolving through this endpoint removes that chicken-and-egg. Returns `agentUuid: null`
 * until it resolves (or if the agent can't be provisioned) — callers gate on `ready`.
 */
export function useSystemAgent(agentId: string): UseSystemAgentResult {
  const { instanceId } = useInstanceId();

  const { data, isPending, error } = useQuery({
    queryKey: ['system-agent', agentId, instanceId ?? ''],
    queryFn: () => ensureSystemAgent(instanceId ?? '', agentId),
    enabled: !!instanceId && !!agentId,
    staleTime: Infinity, // resolve once per session; the endpoint is idempotent
    // The app-level QueryClient has no default overrides, so React-Query's
    // built-in retry:3 would fire up to 4 POSTs on any 5xx — layered with the
    // http client's 429 retry that can reach ~12 requests in the worst case.
    // The endpoint is idempotent so this is not a correctness bug, but it
    // multiplies Sentry noise and prod cost. Same shape as useAgentDirectory.
    retry: false,
  });

  const agentUuid = data?.agent_uuid ?? null;

  return { agentUuid, ready: !!agentUuid, isPending, error };
}
